(ns ui.view.list-opensearch.event
  (:require
   [common.localization :as loc]
   [common.opensearch.utils]
   [common.schema.registry :as registry]
   [fx.ajax :as ajax]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [ui.data.query-results.event :as query-results-event]
   [ui.view.list-opensearch.model :as model]
   [ui.view.opensearch-value.currency :as currency]))

(defn- registry-field-title
  [field-path]
  (let [attribute (if (sequential? field-path)
                    (last field-path)
                    field-path)
        field-schema (when attribute
                       (registry/get-field-schema nil attribute))]
    (when attribute
      (loc/field-name attribute field-schema))))

(defn compile-card-field
  [field-path]
  (let [attribute (if (sequential? field-path)
                    (last field-path)
                    field-path)
        field-schema (when attribute
                       (registry/get-field-schema nil attribute))
        alias (common.opensearch.utils/field-path->alias field-path)
        currency-field-path (currency/currency-field-path {:field-path   field-path
                                                           :field-schema field-schema})]
    {:field-path field-path
     :field-title (registry-field-title field-path)
     :data-type (:field/data-type field-schema)
     :number-multiplier (:field/number-multiplier field-schema)
     :alias alias
     :flat-path ["flat" alias]
     :label-path ["labels" (str alias "_en")]
     :currency-flat-path (when currency-field-path
                           ["flat" (common.opensearch.utils/field-path->alias currency-field-path)])}))

(defn compile-card-config
  [card-config]
  (let [compile-vec (fn [xs] (mapv compile-card-field (or xs [])))]
    {:header-fields (compile-vec (:header-fields card-config))
     :body-fields (compile-vec (:body-fields card-config))
     :footer-fields (compile-vec (:footer-fields card-config))
     :show-labels? (if (contains? card-config :show-labels?)
                     (boolean (:show-labels? card-config))
                     true)
     :highlight (when-let [hp (:highlight card-config)]
                  (compile-card-field hp))
     :title-fallback (when-let [tf (:title-fallback card-config)]
                       (compile-card-field tf))}))

(defn card-config->field-paths
  [card-config]
  (->> (concat (:header-fields card-config)
               (:body-fields card-config)
               (:footer-fields card-config)
               (:currency-fields card-config)
               (when-let [hp (:highlight card-config)] [hp])
               (when-let [tf (:title-fallback card-config)] [tf]))
       (remove nil?)
       distinct
       vec))

(defn init-runtime
  [config]
  (let [card-config (:card-config config)]
    {model/config-key config
     model/compiled-config-key {:card-config (compile-card-config card-config)
                                :fields (card-config->field-paths card-config)}
     model/items-key []
     model/status-key :idle
     model/error-key nil
     model/total-key 0
     model/next-offset-key 0
     model/has-more-key true
     model/groups-key {:status :idle
                       :order []
                       :by-value {}}
     model/items-by-group-key {}}))

(def ^:private dropped ::dropped)

(defn- transit-safe
  [x]
  (cond
    (or (nil? x)
        (string? x)
        (number? x)
        (keyword? x)
        (boolean? x))
    x

    (vector? x)
    (->> x
         (map transit-safe)
         (remove #{dropped})
         vec)

    (set? x)
    (->> x
         (map transit-safe)
         (remove #{dropped})
         vec)

    (sequential? x)
    (->> x
         (map transit-safe)
         (remove #{dropped})
         vec)

    (map? x)
    (->> x
         (keep (fn [[k v]]
                 (let [k* (transit-safe k)
                       v* (transit-safe v)]
                   (when (and (not= k* dropped)
                              (not= v* dropped))
                     [k* v*]))))
         (into {}))

    :else
    dropped))

(defn hits->items
  "The documents of the hits of a search response, as cards: the source of each hit with
   the id of the entity in `original.db/id`. A hit that has no id keeps the source as it is."
  [hits]
  (->> (or hits [])
       (mapv (fn [hit]
               (let [source (or (get hit :_source)
                                (get hit "_source")
                                hit)
                     hit-id (or (get hit :_id)
                                (get hit "_id")
                                (get source "original.db/id")
                                (get source :original.db/id))
                     parsed-id (cond
                                 (number? hit-id) hit-id
                                 (string? hit-id) (let [n (js/parseInt hit-id 10)]
                                                    (when-not (js/isNaN n)
                                                      n))
                                 :else nil)]
                 (cond-> source
                   parsed-id
                   (assoc "original.db/id" parsed-id)))))))

(defn- ensure-group-state
  [db list-id group-value]
  (let [path (into (model/items-by-group-path list-id) [group-value])]
    (if (get-in db path)
      db
      (assoc-in db path {:status :idle
                         :error nil
                         :items []
                         :next-offset nil
                         :total nil
                         :has-more? true}))))

(def ^:private initial-group-page-load-limit 8)
(def ^:private group-page-size 30)

(defn- group-loadable?
  [groups-by-value group-value]
  (let [count (:count (get groups-by-value group-value))]
    (or (nil? count)
        (pos? count))))

(defn- mark-empty-groups-loaded
  [db list-id group-order groups-by-value]
  (reduce (fn [db group-value]
            (if (group-loadable? groups-by-value group-value)
              db
              (assoc-in db
                        (into (model/items-by-group-path list-id) [group-value])
                        {:status :loaded
                         :error nil
                         :items []
                         :next-offset nil
                         :total 0
                         :has-more? false})))
          db
          group-order))

(defn- first-loadable-groups
  [group-order groups-by-value]
  (->> group-order
       (filter (fn [group-value]
                 (group-loadable? groups-by-value group-value)))
       (take initial-group-page-load-limit)
       vec))

(rf/reg-event-fx
 ::init
 (fn [{:keys [db]} [_ {:keys [list-id config]}]]
   (let [runtime (init-runtime config)
         grouped? (some? (:group-by-field config))]
     {:db (assoc-in db (model/list-path list-id) runtime)
      :fx (if grouped?
            [[:dispatch [::load-groups {:list-id list-id}]]]
            [[:dispatch [::load-page {:list-id list-id
                                      :start-index 0
                                      :count 50}]]])})))

(rf/reg-event-db
 ::destroy
 (fn [db [_ {:keys [list-id]}]]
   (potpuri/dissoc-in db (model/list-path list-id))))

(rf/reg-event-fx
 ::load-page
 (fn [{:keys [db]} [_ {:keys [list-id start-index count]}]]
   (let [config (get-in db (model/config-path list-id))
         fields (get-in db (into (model/compiled-config-path list-id) [:fields]))
         {:keys [entity-type pipeline-id opensearch-queries sort]} config
         count (or count 50)
         start-index (or start-index 0)
         request-body {:entity-type entity-type
                       :pipeline-id pipeline-id
                       :opensearch-queries opensearch-queries
                       :sort sort
                       :fields fields
                       :from start-index
                       :size count}
         query-key (query-results-event/request->query-key {:source :list
                                                            :request request-body})
         cache-args {:list-id list-id
                     :start-index start-index
                     :query-key query-key
                     :request request-body
                     :refresh-event [::load-page {:list-id list-id
                                                  :start-index start-index
                                                  :count count}]}]
     (if (and entity-type
              (seq fields))
       {:db (query-results-event/record-loading (-> db
                (assoc-in (into (model/list-path list-id) [model/status-key]) :loading)
                (assoc-in (into (model/list-path list-id) [model/error-key]) nil))
                 query-key {:source :list
                            :request request-body
                            :refresh-event [::load-page {:list-id list-id
                                                         :start-index start-index
                                                         :count count}]})
        ::ajax/send {:operation :search/search
                     :body request-body
                     :timeout-ms 60000
                     :on-ok [::load-page-ok cache-args]
                     :on-error [::load-page-failed cache-args]}}
       {:db (-> db
                (assoc-in (into (model/list-path list-id) [model/status-key]) :idle)
                (assoc-in (into (model/list-path list-id) [model/error-key])
                          {:cause :missing-search-config
                           :entity-type entity-type
                           :fields fields}))}))))

(rf/reg-event-fx
 ::load-groups
 (fn [{:keys [db]} [_ {:keys [list-id]}]]
   (let [config (get-in db (model/config-path list-id))
         {:keys [entity-type pipeline-id group-by-field hide-empty-columns? opensearch-queries kanban-column-sort-mode]} config
         body {:entity-type entity-type
               :pipeline-id pipeline-id
               :group-by-field group-by-field
               :hide-empty-columns? (boolean hide-empty-columns?)
               :opensearch-queries (transit-safe opensearch-queries)
               :limit 200
               :order kanban-column-sort-mode}
         query-key (query-results-event/request->query-key {:source :list-groups
                                                            :request body})
         cache-args {:list-id list-id
                     :query-key query-key
                     :request body
                     :refresh-event [::load-groups {:list-id list-id}]}]
     (if (and entity-type group-by-field)
       {:db (query-results-event/record-loading (assoc-in db (into (model/groups-path list-id) [:status]) :loading)
                                                query-key {:source :list-groups
                                                           :request body
                                                           :refresh-event [::load-groups {:list-id list-id}]})
        ::ajax/send {:operation :search/kanban-columns
                     :body body
                     :on-ok [::load-groups-ok cache-args]
                     :on-error [::load-groups-failed cache-args]}}
       {:db (-> db
                (assoc-in (into (model/groups-path list-id) [:status]) :error)
                (assoc-in (into (model/list-path list-id) [model/error-key])
                          {:cause :missing-group-config
                           :entity-type entity-type
                           :group-by-field group-by-field}))}))))

(rf/reg-event-fx
 ::load-groups-ok
 (fn [{:keys [db]} [_ {:keys [list-id query-key request refresh-event]} resp]]
   (let [columns (or (get-in resp [:body :columns])
                     (get-in resp [:body :buckets])
                     [])
         order (mapv :value columns)
         by-value (->> columns
                       (map (fn [{:keys [value label count] :as raw}]
                              [value {:value value
                                      :label label
                                      :count count
                                      :raw raw}]))
                       (into {}))
         db* (-> db
                 (assoc-in (model/groups-path list-id) {:status :loaded
                                                        :order order
                                                        :by-value by-value})
                 (assoc-in (into (model/list-path list-id) [model/error-key]) nil)
                 (assoc-in (into (model/list-path list-id) [model/status-key]) :loaded)
                 (assoc-in (into (model/list-path list-id) [model/total-key])
                           (reduce (fn [acc col]
                                     (+ acc (or (:count col) 0)))
                                   0
                                   columns)))]
     {:db (cond-> (mark-empty-groups-loaded db* list-id order by-value)
            (string? query-key)
            (query-results-event/record-result query-key {:source :list-groups
                                                          :request request
                                                          :refresh-event refresh-event
                                                          :rows columns
                                                          :total (reduce (fn [acc col]
                                                                           (+ acc (or (:count col) 0)))
                                                                         0
                                                                         columns)
                                                          :facets columns
                                                          :raw resp
                                                          :result-meta {:list-id list-id}}))
      :fx (->> (first-loadable-groups order by-value)
               (mapv (fn [group-value]
                       [:dispatch [::load-group-page {:list-id list-id
                                                      :group-value group-value
                                                      :start-index 0
                                                      :count group-page-size}]])))})))

(rf/reg-event-db
 ::load-groups-failed
 (fn [db [_ {:keys [list-id query-key]} err]]
   (cond-> (-> db
       (assoc-in (into (model/groups-path list-id) [:status]) :error)
       (assoc-in (into (model/list-path list-id) [model/error-key]) err))
     (string? query-key)
     (query-results-event/record-error query-key err))))

(rf/reg-event-fx
 ::load-group-page
 (fn [{:keys [db]} [_ {:keys [list-id group-value start-index count]}]]
   (let [config (get-in db (model/config-path list-id))
         fields (get-in db (into (model/compiled-config-path list-id) [:fields]))
         column-meta (get-in db (into (model/groups-path list-id) [:by-value group-value :raw]))
         {:keys [entity-type pipeline-id group-by-field opensearch-queries sort]} config
         count (or count group-page-size)
         body {:entity-type entity-type
               :pipeline-id pipeline-id
               :group-by-field group-by-field
               :opensearch-queries (transit-safe opensearch-queries)
               :sort sort
               :column (merge {:value group-value
                               :limit count
                               :cursor start-index}
                              (->> (select-keys (or column-meta {})
                                                [:label :from :to])
                                   (remove (fn [[_k v]]
                                             (nil? v)))
                                   (into {})))
               :fields fields}
         query-key (query-results-event/request->query-key {:source :list-group-page
                                                            :request body})
         cache-args {:list-id list-id
                     :group-value group-value
                     :start-index start-index
                     :query-key query-key
                     :request body
                     :refresh-event [::load-group-page {:list-id list-id
                                                        :group-value group-value
                                                        :start-index start-index
                                                        :count count}]}
         db* (-> db
                 (ensure-group-state list-id group-value)
                 (assoc-in (into (model/items-by-group-path list-id) [group-value :status]) :loading)
                 (assoc-in (into (model/items-by-group-path list-id) [group-value :error]) nil))]
     (if (and entity-type group-by-field (seq fields))
       {:db (query-results-event/record-loading db* query-key {:source :list-group-page
                                                                :request body
                                                                :refresh-event [::load-group-page {:list-id list-id
                                                                                                   :group-value group-value
                                                                                                   :start-index start-index
                                                                                                   :count count}]})
        ::ajax/send {:operation :search/kanban-column
                     :body body
                     :on-ok [::load-group-page-ok cache-args]
                     :on-error [::load-group-page-failed cache-args]}}
       {:db (-> db*
                (assoc-in (into (model/items-by-group-path list-id) [group-value :status]) :error)
                (assoc-in (into (model/items-by-group-path list-id) [group-value :error])
                          {:cause :missing-search-config}))}))))

(rf/reg-event-db
 ::load-group-page-ok
 (fn [db [_ {:keys [list-id group-value start-index query-key request refresh-event]} resp]]
   (let [column (get-in resp [:body :column])
         hits (or (get-in resp [:body :cards])
                  (get-in column [:hits])
                  (get-in resp [:body :hits])
                  [])
         items (hits->items hits)
         next-offset (or (get-in resp [:body :next-cursor])
                         (get-in column [:next-cursor]))
         raw-has-more? (or (get-in resp [:body :has-more?])
                           (get-in column [:has-more?]))
         has-more? (if (some? raw-has-more?)
                     (boolean raw-has-more?)
                     (boolean next-offset))
         total (or (get-in column [:total])
                   (get-in resp [:body :total]))
         items-path (into (model/items-by-group-path list-id) [group-value :items])
         db* (-> db
         (ensure-group-state list-id group-value)
         (update-in items-path
                    (fn [existing]
                      (if (zero? start-index)
                        (vec items)
                        (into (vec (or existing [])) items))))
         (assoc-in (into (model/items-by-group-path list-id) [group-value :next-offset]) next-offset)
         (assoc-in (into (model/items-by-group-path list-id) [group-value :has-more?]) has-more?)
         (assoc-in (into (model/items-by-group-path list-id) [group-value :total]) total)
         (assoc-in (into (model/items-by-group-path list-id) [group-value :status]) :loaded)
         (assoc-in (into (model/items-by-group-path list-id) [group-value :error]) nil))]
     (cond-> db*
       (string? query-key)
       (query-results-event/record-result query-key {:source :list-group-page
                                                     :request request
                                                     :refresh-event refresh-event
                                                     :rows items
                                                     :total total
                                                     :raw resp
                                                     :result-meta {:list-id list-id
                                                                   :group-value group-value
                                                                   :next-offset next-offset
                                                                   :has-more? has-more?}})))))

(rf/reg-event-db
 ::load-group-page-failed
 (fn [db [_ {:keys [list-id group-value query-key]} err]]
   (cond-> (-> db
       (ensure-group-state list-id group-value)
       (assoc-in (into (model/items-by-group-path list-id) [group-value :status]) :error)
       (assoc-in (into (model/items-by-group-path list-id) [group-value :error]) err))
     (string? query-key)
     (query-results-event/record-error query-key err))))

(rf/reg-event-fx
 ::load-next-group-page
 (fn [{:keys [db]} [_ {:keys [list-id expanded-groups]}]]
   (let [group-order (get-in db (into (model/groups-path list-id) [:order]) [])
         groups-by-value (get-in db (into (model/groups-path list-id) [:by-value]) {})
         expanded-groups (set (or expanded-groups group-order))
         unloaded-candidate (some (fn [group-value]
                                    (let [group-state (get-in db (into (model/items-by-group-path list-id) [group-value]))]
                                      (when (and (contains? expanded-groups group-value)
                                                 (group-loadable? groups-by-value group-value)
                                                 (not= :loading (:status group-state))
                                                 (or (nil? group-state)
                                                     (= :idle (:status group-state))))
                                        {:group-value group-value
                                         :next-offset 0})))
                                  group-order)
         paged-candidate (some (fn [group-value]
                                 (let [group-state (get-in db (into (model/items-by-group-path list-id) [group-value]))]
                                   (when (and (contains? expanded-groups group-value)
                                              (not= :loading (:status group-state))
                                              (true? (:has-more? group-state))
                                              (some? (:next-offset group-state)))
                                     {:group-value group-value
                                      :next-offset (:next-offset group-state)})))
                               group-order)
         candidate (or unloaded-candidate paged-candidate)]
     (if candidate
       {:fx [[:dispatch [::load-group-page {:list-id list-id
                                            :group-value (:group-value candidate)
                                            :start-index (:next-offset candidate)
                                            :count group-page-size}]]]}
       {}))))

(rf/reg-event-db
 ::load-page-ok
 (fn [db [_ {:keys [list-id start-index query-key request refresh-event]} resp]]
   (let [hits (get-in resp [:hits :hits] [])
         total (get-in resp [:hits :total :value] 0)
         items (mapv (fn [hit]
                       (let [source (:_source hit)]
                         (assoc source "original.db/id" (js/parseInt (:_id hit)))))
                     hits)
         append? (pos? start-index)
         current-items (get-in db (into (model/list-path list-id) [model/items-key]) [])
         merged-items (if append?
                        (into (vec current-items) items)
                        items)
         next-offset (+ start-index (count items))
         has-more? (< next-offset total)
         db* (-> db
         (assoc-in (into (model/list-path list-id) [model/items-key]) merged-items)
         (assoc-in (into (model/list-path list-id) [model/status-key]) :loaded)
         (assoc-in (into (model/list-path list-id) [model/total-key]) total)
         (assoc-in (into (model/list-path list-id) [model/next-offset-key]) next-offset)
         (assoc-in (into (model/list-path list-id) [model/has-more-key]) has-more?))]
     (cond-> db*
       (string? query-key)
       (query-results-event/record-result query-key {:source :list
                                                     :request request
                                                     :refresh-event refresh-event
                                                     :rows items
                                                     :total total
                                                     :raw resp
                                                     :result-meta {:list-id list-id
                                                                   :start-index start-index
                                                                   :next-offset next-offset
                                                                   :has-more? has-more?}})))))

(rf/reg-event-db
 ::load-page-failed
 (fn [db [_ {:keys [list-id query-key]} err]]
   (cond-> (-> db
       (assoc-in (into (model/list-path list-id) [model/status-key]) :error)
       (assoc-in (into (model/list-path list-id) [model/error-key]) err))
     (string? query-key)
     (query-results-event/record-error query-key err))))
